import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, FileCheck2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { services, process, company, whyChoose } from '../mock';

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = services.find(s => s.slug === slug);
  if (!svc) return <Navigate to="/services" replace />;
  const related = services.filter(s => s.slug !== slug && s.category === svc.category).slice(0, 3);
  const Icon = svc.icon;

  return (
    <>
      <section className="relative pt-40 pb-20 bg-[#0B1B33] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="absolute -top-20 right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link to="/services" className="inline-flex items-center text-sm text-slate-400 hover:text-amber-400 transition">
              <ArrowLeft className="w-4 h-4 mr-1" /> All services
            </Link>
            <Badge className="mt-4 bg-amber-500/10 text-amber-400 hover:bg-amber-500/10 border border-amber-400/20 px-3 py-1">{svc.category}</Badge>
            <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
              {svc.title}
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-xl leading-relaxed">{svc.summary}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-6 rounded-xl">
                  Request a quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href={`tel:${company.phone}`}>
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-amber-400 h-12 px-6 rounded-xl">
                  <Phone className="w-4 h-4 mr-2" /> {company.phone}
                </Button>
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img src={svc.image} alt={svc.title} className="w-full h-[400px] object-cover rounded-3xl" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-2xl flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#0B1B33]" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Specialty</div>
                  <div className="font-bold text-[#0B1B33]">{svc.category}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview + bullets */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 px-3 py-1">Overview</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold text-[#0B1B33] tracking-tight leading-tight">What’s included in your {svc.title.toLowerCase()} package.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{svc.description}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Every {svc.title.toLowerCase()} engagement includes a senior-estimator QA pass, a clean Excel + PDF deliverable, and bid-day phone support — at one fixed fee.
            </p>
          </div>
          <div className="lg:col-span-5">
            <Card className="p-7 rounded-2xl bg-slate-50 border-slate-100">
              <h3 className="font-bold text-[#0B1B33]">Scope coverage</h3>
              <ul className="mt-5 space-y-3">
                {svc.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm">{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-slate-200 flex items-center gap-3">
                <FileCheck2 className="w-6 h-6 text-amber-500" />
                <div className="text-xs text-slate-600">Deliverables in <b>CSI MasterFormat</b> — Excel + PDF + takeoff backup.</div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="bg-[#0B1B33] text-amber-400 px-3 py-1 hover:bg-[#0B1B33]">How we work</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold text-[#0B1B33] tracking-tight">A predictable, transparent process.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {process.map((p, i) => (
              <Card key={i} className="p-7 rounded-2xl bg-white border-slate-100 hover:shadow-lg transition">
                <div className="text-amber-500 font-mono text-xs">STEP {p.step}</div>
                <h3 className="mt-2 text-lg font-bold text-[#0B1B33]">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{p.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose mini */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1B33] tracking-tight">Why choose Core for {svc.title.toLowerCase()}?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyChoose.slice(0, 3).map((w, i) => (
              <Card key={i} className="p-7 rounded-2xl border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                  <w.icon className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="mt-4 font-bold text-[#0B1B33]">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{w.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#0B1B33] tracking-tight mb-10">Related services</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link to={`/services/${r.slug}`} key={r.slug} className="group">
                  <Card className="bg-white rounded-2xl overflow-hidden border-slate-100 hover:shadow-xl transition">
                    <img src={r.image} alt={r.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="p-5">
                      <h3 className="font-bold text-[#0B1B33] group-hover:text-amber-600 transition">{r.title}</h3>
                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">{r.summary}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-[#0B1B33] rounded-3xl p-10 md:p-14 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Ready to price your next {svc.title.toLowerCase()} job?</h2>
            <p className="mt-3 text-slate-300 max-w-2xl mx-auto">Send drawings — we’ll come back with scope, lead time and a fixed fee in 4 business hours.</p>
            <Link to="/contact" className="inline-block mt-7">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-7 rounded-xl">
                Request a quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
